// Shorten Unity's raw OS string to something a person approving a sign-in can read.
//
// deviceOS carries SystemInfo.operatingSystem verbatim, which is a diagnostic
// string, not a label:
//
//   Android OS 17 / API-37 (CP2A.260705.006/15641320)
//   Windows 11  (10.0.22631) 64bit
//   Linux 6.5.0-27-generic Ubuntu 22.04 64bit
//
// On the confirm screen that third line is supposed to say which OS the device
// is running. An API level and a build fingerprint answer a question nobody is
// asking at that moment, and they push the line to two rows on a narrow modal.
//
// This only changes what is DISPLAYED. The full string still travels in the
// Edxr-User-Agent payload and is what the server records, so the build number
// is there when someone is actually diagnosing a device.
//
// Anything unrecognised falls through to a conservative tidy rather than a
// guess: an OS we have never seen is better shown imperfectly than mislabelled.

const RULES = [
  // Android OS 17 / API-37 (CP2A.260705.006/15641320)  ->  Android 17
  { re: /^Android(?:\s+OS)?\s+([\d.]+)/i, out: m => `Android ${m[1]}` },

  // iOS 17.4.1 / iPhone OS 17.4.1  ->  iOS 17.4.1
  { re: /^(?:iOS|iPhone\s+OS)\s+([\d.]+)/i, out: m => `iOS ${m[1]}` },
  { re: /^iPadOS\s+([\d.]+)/i, out: m => `iPadOS ${m[1]}` },

  // Mac OS X 14.4.1  ->  macOS 14.4.1  (Unity still reports the old name)
  { re: /^Mac\s*OS\s*X\s*([\d.]+)/i, out: m => `macOS ${m[1]}` },
  { re: /^macOS\s*([\d.]+)/i, out: m => `macOS ${m[1]}` },

  // Windows 11  (10.0.22631) 64bit  ->  Windows 11
  // The NT build in parentheses is the same information twice for anyone who
  // does not already know the mapping, and useless to anyone who does not.
  { re: /^Windows\s+([\w.]+)/i, out: m => `Windows ${m[1]}` },

  // Linux 6.5.0-27-generic Ubuntu 22.04 64bit  ->  Ubuntu 22.04
  // The distribution is what a person recognises; the kernel revision is not.
  { re: /\b(Ubuntu|Debian|Fedora|Arch|Manjaro|Mint|SUSE|CentOS|Red\s*Hat)\b\s*([\d.]*)/i,
    out: m => (m[2] ? `${m[1]} ${m[2]}` : m[1]) },
  { re: /^Linux\b/i, out: () => 'Linux' }
]

/**
 * A display label for a raw OS string. Returns null for empty input so callers
 * can keep their existing "no OS line" behaviour.
 */
export const shortOsLabel = raw => {
  const text = (raw || '').trim()
  if (!text) return null

  for (const rule of RULES) {
    const m = text.match(rule.re)
    if (m) return rule.out(m)
  }

  // Unrecognised: drop parenthesised build detail and the bit-width suffix,
  // collapse the whitespace Unity leaves behind, and cap the length so one
  // strange value cannot blow out the modal.
  const tidied = text
    .replace(/\([^)]*\)/g, ' ')
    .replace(/\b(?:32|64)\s*-?bit\b/gi, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim()

  if (!tidied) return null
  return tidied.length > 40 ? `${tidied.slice(0, 39).trimEnd()}…` : tidied
}
